import { toast } from "sonner";

export interface LiveToastOptions {
  description?: string;
  action?: { label: string; onClick: () => void };
}

/**
 * One toast that is rewritten in place instead of a new toast per state change.
 *
 * `sonner` keys a toast by its `id`: calling `toast.loading` / `toast.success` /
 * `toast.error` again with the same id REPLACES the visible toast rather than
 * stacking another one. That is the whole mechanism behind the run tracker's
 * "Running… → Finished" toast, and the batch collector needs exactly the same
 * behaviour for "Adding 5 colours… 3 of 5 → 4 created · 1 skipped · 0 failed" —
 * so it lives here once rather than being retyped in both.
 */
export interface LiveToast {
  readonly id: string;
  loading: (title: string, options?: LiveToastOptions) => void;
  success: (title: string, options?: LiveToastOptions) => void;
  error: (title: string, options?: LiveToastOptions) => void;
}

export const createLiveToast = (id: string): LiveToast => ({
  id,
  loading: (title, options) => {
    toast.loading(title, { id, ...options });
  },
  success: (title, options) => {
    toast.success(title, { id, ...options });
  },
  error: (title, options) => {
    toast.error(title, { id, ...options });
  },
});
